import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const YEARS = ['2024','2025','2026'];
const FY_OPTIONS = ['2023-24', '2024-25', '2025-26', '2026-27'];

// ================ GST Returns Tab ================
function GstReturnsTab() {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({
    client_id: '', client_name: '', month: '', year: '',
    gstr1_status: 'Pending', gstr1_date: '', gstr1_arn: '', gstr1_notes: '',
    gstr3b_status: 'Pending', gstr3b_date: '', gstr3b_arn: '', gstr3b_notes: '',
    fees_collected: 0, gst_amendment: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set('month', monthFilter);
      if (yearFilter) params.set('year', yearFilter);
      if (searchText) params.set('search', searchText);
      const [recRes, clRes] = await Promise.all([
        api.get(`/gst-pt?${params}`),
        api.get('/clients?limit=500&client_type=GST+%26+PT')
      ]);
      setRecords(recRes.data);
      setClients(clRes.data.clients || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [monthFilter, yearFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    const now = new Date();
    setEditId(null);
    setForm({
      client_id: '', client_name: '', month: String(now.getMonth() + 1).padStart(2, '0'), year: String(now.getFullYear()),
      gstr1_status: 'Pending', gstr1_date: '', gstr1_arn: '', gstr1_notes: '',
      gstr3b_status: 'Pending', gstr3b_date: '', gstr3b_arn: '', gstr3b_notes: '',
      fees_collected: 0, gst_amendment: '', notes: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditId(rec._id);
    setForm({
      client_id: rec.client_id, client_name: rec.client_name, month: rec.month, year: rec.year,
      gstr1_status: rec.gstr1_status, gstr1_date: rec.gstr1_date || '', gstr1_arn: rec.gstr1_arn || '', gstr1_notes: rec.gstr1_notes || '',
      gstr3b_status: rec.gstr3b_status, gstr3b_date: rec.gstr3b_date || '', gstr3b_arn: rec.gstr3b_arn || '', gstr3b_notes: rec.gstr3b_notes || '',
      fees_collected: rec.fees_collected || 0, gst_amendment: rec.gst_amendment || '', notes: rec.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.month || !form.year) { toast.error('Client, month and year required'); return; }
    try {
      if (editId) { await api.put(`/gst-pt/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/gst-pt', form); toast.success('Created'); }
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try { await api.delete(`/gst-pt/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };


  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search client, ARN..."
          testId="gst-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={monthFilter || 'all'} onValueChange={(v) => setMonthFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-11 input-field"><SelectValue placeholder="All Months" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Months</SelectItem>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={yearFilter || 'all'} onValueChange={(v) => setYearFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-11 input-field"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Years</SelectItem>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto">
          <ExportMenu
            endpoint="/exports/gst"
            params={{ ...(yearFilter ? { year: yearFilter } : {}), ...(monthFilter ? { month: monthFilter } : {}), ...(searchText ? { search: searchText } : {}) }}
            filename="gst_returns"
            testIdPrefix="gst"
          />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-gst-btn"><Plus size={16} /> New Record</Button>
        </div>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="gst-table">
            <thead>
              <tr className="bg-[#1B2A1B] text-white">
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Client</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Month/Year</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">GSTR-1</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">GSTR-1 ARN</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">GSTR-3B</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">GSTR-3B ARN</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Fees</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-16">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-olive-400">Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-olive-400">No records found</td></tr>
              ) : records.map(rec => (
                <tr key={rec._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7]">
                  <td className="py-2 px-3 font-medium text-[#111B11]">{rec.client_name}</td>
                  <td className="py-2 px-3">{MONTH_NAMES[parseInt(rec.month) - 1]} {rec.year}</td>
                  <td className="py-2 px-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${rec.gstr1_status === 'Filed' ? 'bg-green-100 text-green-700' : rec.gstr1_status === 'N/A' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>{rec.gstr1_status}</span></td>
                  <td className="py-2 px-3 font-mono text-xs text-[#3B5A3B]">{rec.gstr1_arn || '-'}</td>
                  <td className="py-2 px-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${rec.gstr3b_status === 'Filed' ? 'bg-green-100 text-green-700' : rec.gstr3b_status === 'N/A' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>{rec.gstr3b_status}</span></td>
                  <td className="py-2 px-3 font-mono text-xs text-[#3B5A3B]">{rec.gstr3b_arn || '-'}</td>
                  <td className="py-2 px-3 text-right font-mono">{(rec.fees_collected || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-0.5">
                      <button onClick={() => openEdit(rec)} className="p-1 hover:bg-olive-100 rounded-sm"><Pencil size={13} className="text-olive-500" /></button>
                      <button onClick={() => handleDelete(rec._id)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {records.length > 0 && (
                <tr className="bg-[#F4F7F4] border-t-2 border-[#CCD9CC] font-semibold">
                  <td colSpan={6} className="py-2.5 px-3 text-right text-xs uppercase tracking-wider text-[#4F734F]">
                    Total Fees ({records.length} records)
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-[#111B11]">
                    {records.reduce((s, r) => s + (r.fees_collected || 0), 0).toLocaleString('en-IN')}
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* GST Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} GST Return</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client</Label>
                <Select value={form.client_id || 'none'} onValueChange={(v) => {
                  const c = clients.find(cl => cl._id === v);
                  setForm(prev => ({ ...prev, client_id: v, client_name: c?.full_name || '' }));
                }}>
                  <SelectTrigger className="input-field"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Month</Label>
                <Select value={form.month} onValueChange={(v) => setForm(prev => ({ ...prev, month: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Year</Label>
                <Select value={form.year} onValueChange={(v) => setForm(prev => ({ ...prev, year: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* GSTR-1 Section */}
            <div className="border border-[#E6EDE6] rounded-sm p-4">
              <h3 className="text-sm font-semibold text-[#213021] mb-3">GSTR-1</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">Status</Label>
                  <Select value={form.gstr1_status} onValueChange={(v) => setForm(prev => ({ ...prev, gstr1_status: v }))}><SelectTrigger className="input-field"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Filed">Filed</SelectItem><SelectItem value="N/A">N/A</SelectItem></SelectContent></Select></div>
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">Date Filed</Label>
                  <Input type="date" value={form.gstr1_date} onChange={(e) => setForm(prev => ({ ...prev, gstr1_date: e.target.value }))} className="input-field" /></div>
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">ARN Number</Label>
                  <Input value={form.gstr1_arn} onChange={(e) => setForm(prev => ({ ...prev, gstr1_arn: e.target.value }))} className="input-field font-mono" placeholder="ARN number" data-testid="gstr1-arn-input" /></div>
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">Notes</Label>
                  <Input value={form.gstr1_notes} onChange={(e) => setForm(prev => ({ ...prev, gstr1_notes: e.target.value }))} className="input-field" /></div>
              </div>
            </div>

            {/* GSTR-3B Section */}
            <div className="border border-[#E6EDE6] rounded-sm p-4">
              <h3 className="text-sm font-semibold text-[#213021] mb-3">GSTR-3B</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">Status</Label>
                  <Select value={form.gstr3b_status} onValueChange={(v) => setForm(prev => ({ ...prev, gstr3b_status: v }))}><SelectTrigger className="input-field"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Filed">Filed</SelectItem><SelectItem value="N/A">N/A</SelectItem></SelectContent></Select></div>
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">Date Filed</Label>
                  <Input type="date" value={form.gstr3b_date} onChange={(e) => setForm(prev => ({ ...prev, gstr3b_date: e.target.value }))} className="input-field" /></div>
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">ARN Number</Label>
                  <Input value={form.gstr3b_arn} onChange={(e) => setForm(prev => ({ ...prev, gstr3b_arn: e.target.value }))} className="input-field font-mono" placeholder="ARN number" data-testid="gstr3b-arn-input" /></div>
                <div className="space-y-1"><Label className="text-xs text-[#4F734F]">Notes</Label>
                  <Input value={form.gstr3b_notes} onChange={(e) => setForm(prev => ({ ...prev, gstr3b_notes: e.target.value }))} className="input-field" /></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Fees Collected</Label>
                <Input type="number" value={form.fees_collected} onChange={(e) => setForm(prev => ({ ...prev, fees_collected: Number(e.target.value) }))} className="input-field" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">GST Amendment</Label>
                <Input value={form.gst_amendment} onChange={(e) => setForm(prev => ({ ...prev, gst_amendment: e.target.value }))} className="input-field" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" /></div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-gst-btn">{editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================ Professional Tax Tab ================
function ProfessionalTaxTab() {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fyFilter, setFyFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({
    client_id: '', client_name: '', financial_year: '2025-26',
    ptr_number: '', filing_date: '', status: 'Pending',
    amount: 0, payment_mode: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (fyFilter) params.set('financial_year', fyFilter);
      if (searchText) params.set('search', searchText);
      const [recRes, clRes] = await Promise.all([
        api.get(`/professional-tax?${params}`),
        api.get('/clients?limit=500')
      ]);
      setRecords(recRes.data);
      setClients(clRes.data.clients || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [fyFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({
      client_id: '', client_name: '', financial_year: '2025-26',
      ptr_number: '', filing_date: '', status: 'Pending',
      amount: 0, payment_mode: '', notes: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (rec) => {
    setEditId(rec._id);
    setForm({
      client_id: rec.client_id, client_name: rec.client_name,
      financial_year: rec.financial_year || '', ptr_number: rec.ptr_number || '',
      filing_date: rec.filing_date || '', status: rec.status || 'Pending',
      amount: rec.amount || 0, payment_mode: rec.payment_mode || '',
      notes: rec.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.financial_year) { toast.error('Client and FY required'); return; }
    try {
      if (editId) { await api.put(`/professional-tax/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/professional-tax', form); toast.success('Created'); }
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/professional-tax/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search client, PTR..."
          testId="pt-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={fyFilter || 'all'} onValueChange={(v) => setFyFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-11 input-field"><SelectValue placeholder="All FY" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All FY</SelectItem>{FY_OPTIONS.map(fy => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto">
          <ExportMenu
            endpoint="/exports/pt"
            params={{ ...(fyFilter ? { financial_year: fyFilter } : {}), ...(searchText ? { search: searchText } : {}) }}
            filename="professional_tax"
            testIdPrefix="pt"
          />
          <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-pt-btn"><Plus size={16} /> New PT Record</Button>
        </div>
      </div>

      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="pt-table">
            <thead>
              <tr className="bg-[#1B2A1B] text-white">
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-10">SL</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Client Name</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Financial Year</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">PTR Number</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Filing Date</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Status</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider text-right">Amount</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Payment</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider">Notes</th>
                <th className="py-2.5 px-3 font-medium text-xs tracking-wider w-16">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8 text-olive-400">Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-8 text-olive-400">No professional tax records found</td></tr>
              ) : records.map((rec, idx) => (
                <tr key={rec._id} className="border-b border-[#E6EDE6] hover:bg-[#F8FAF7]">
                  <td className="py-2 px-3 text-olive-500 font-mono text-xs">{idx + 1}</td>
                  <td className="py-2 px-3 font-medium text-[#111B11]">{rec.client_name}</td>
                  <td className="py-2 px-3">{rec.financial_year}</td>
                  <td className="py-2 px-3 font-mono text-xs text-[#273A27]">{rec.ptr_number || '-'}</td>
                  <td className="py-2 px-3">{rec.filing_date || '-'}</td>
                  <td className="py-2 px-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      rec.status === 'Filed' ? 'bg-green-100 text-green-700' :
                      rec.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>{rec.status}</span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{(rec.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3 text-xs">{rec.payment_mode || '-'}</td>
                  <td className="py-2 px-3 text-xs text-[#3B5A3B] max-w-[200px] truncate">{rec.notes || '-'}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-0.5">
                      <button onClick={() => openEdit(rec)} className="p-1 hover:bg-olive-100 rounded-sm"><Pencil size={13} className="text-olive-500" /></button>
                      <button onClick={() => handleDelete(rec._id)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {records.length > 0 && (
                <tr className="bg-[#F4F7F4] border-t-2 border-[#CCD9CC] font-semibold">
                  <td colSpan={6} className="py-2.5 px-3 text-right text-xs uppercase tracking-wider text-[#4F734F]">Total ({records.length} records)</td>
                  <td className="py-2.5 px-3 text-right font-mono text-[#111B11]">{records.reduce((s, r) => s + (r.amount || 0), 0).toLocaleString('en-IN')}</td>
                  <td colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PT Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Professional Tax Record</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Client</Label>
              <Select value={form.client_id || 'none'} onValueChange={(v) => {
                const c = clients.find(cl => cl._id === v);
                setForm(prev => ({ ...prev, client_id: v, client_name: c?.full_name || '' }));
              }}>
                <SelectTrigger className="input-field" data-testid="pt-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{clients.map(c => <SelectItem key={c._id} value={c._id}>{c.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Financial Year</Label>
                <Select value={form.financial_year} onValueChange={(v) => setForm(prev => ({ ...prev, financial_year: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{FY_OPTIONS.map(fy => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">PTR Number</Label>
                <Input
                  value={form.ptr_number}
                  onChange={(e) => setForm(prev => ({ ...prev, ptr_number: e.target.value }))}
                  className="input-field font-mono"
                  placeholder="Enter PTR number"
                  data-testid="ptr-number-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Filing Date</Label>
                <Input type="date" value={form.filing_date} onChange={(e) => setForm(prev => ({ ...prev, filing_date: e.target.value }))} className="input-field" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Filed">Filed</SelectItem>
                    <SelectItem value="Overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Amount (INR)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm(prev => ({ ...prev, amount: Number(e.target.value) }))} className="input-field" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Payment Mode</Label>
                <Select value={form.payment_mode || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, payment_mode: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="input-field"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" /></div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-pt-btn">{editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================ Main Page ================
export default function GstPtPage() {
  return (
    <div data-testid="gst-pt-page">
      <div className="mb-6">
        <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">GST & Professional Tax</h1>
        <p className="text-sm text-[#4F734F] mt-1">Manage GST returns and Professional Tax filings</p>
      </div>

      <Tabs defaultValue="gst" className="space-y-4">
        <TabsList>
          <TabsTrigger value="gst" data-testid="tab-gst-returns">GST Returns</TabsTrigger>
          <TabsTrigger value="pt" data-testid="tab-professional-tax">Professional Tax</TabsTrigger>
        </TabsList>
        <TabsContent value="gst"><GstReturnsTab /></TabsContent>
        <TabsContent value="pt"><ProfessionalTaxTab /></TabsContent>
      </Tabs>
    </div>
  );
}
